// oxlint-disable react/react-compiler -- React Compiler bails out of this file; remove when fixed
import { keyframes, styled } from "goober";
import type React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  popModal,
  pushModal,
  stackTransform,
  useModalPosition,
} from "./modalStack";

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Dialog = styled("dialog")`
  box-shadow: var(--elevation-high);
  border: 0;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  border-radius: 16px;
  padding: 0;
  transform-origin: center bottom;
  transform: none;
  transition: transform 150ms cubic-bezier(0.16, 1, 0.3, 1);

  &[data-stacked="true"] {
    transform: translateY(calc(var(--modal-offset) * 1px))
      scale(var(--modal-scale));
  }

  /* Stacked modals peek out at the bottom; hide their content so the peek reads
   as a blank stacked card instead of leaking the previous modal's footer into
   the top modal's rounded corners. An alert (e.g. a confirmation) does not
   stack the modal beneath it, so that modal stays visible and is dimmed by the
   alert's own backdrop instead. */
  & > * {
    transition: opacity 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-stacked="true"] > * {
    opacity: 0;
  }

  @starting-style {
    transform: translateY(6px);
  }

  &::backdrop {
    background: var(--modal-backdrop);
    opacity: 0;
  }
  &[data-bottom="true"]::backdrop,
  &[data-alert="true"]::backdrop {
    opacity: 1;
    animation: ${backdropFadeIn} 120ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    & > * {
      transition: none;
    }
    &[data-bottom="true"]::backdrop,
    &[data-alert="true"]::backdrop {
      animation: none;
    }
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  alert?: boolean;
  initialFocusRef?: { readonly current: HTMLElement | null };
};

export function Modal({
  open,
  onClose,
  children,
  style,
  alert = false,
  initialFocusRef,
}: Props) {
  const [el, setEl] = useState<HTMLDialogElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const tokenRef = useRef<symbol | null>(null);
  if (tokenRef.current === null) tokenRef.current = Symbol();
  const token = tokenRef.current;

  const { depth, isTop, isBottom, coveredByAlert } = useModalPosition(token);
  const { offset, scale } = stackTransform(depth);
  const isTopRef = useRef(isTop);
  isTopRef.current = isTop;
  const stacked = !isTop && !coveredByAlert;

  useLayoutEffect(() => {
    if (!open) return;
    pushModal(token, alert);
    return () => popModal(token);
  }, [open, token, alert]);

  useLayoutEffect(() => {
    if (!el) return;
    el.showModal();
    const focusTarget = initialFocusRef?.current ?? el;
    focusTarget.focus();
    if (
      focusTarget instanceof HTMLInputElement ||
      focusTarget instanceof HTMLTextAreaElement
    )
      focusTarget.select();
    const handleClose = () => onCloseRef.current();
    const handleCancel = (e: Event) => e.preventDefault();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isTopRef.current) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onCloseRef.current();
    };
    el.addEventListener("close", handleClose);
    el.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      el.removeEventListener("close", handleClose);
      el.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [el, initialFocusRef]);

  if (!open) return null;

  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    e.stopPropagation();
    if (e.target === el && isTopRef.current) onClose();
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return createPortal(
    <Dialog
      ref={setEl}
      tabIndex={-1}
      data-stacked={stacked}
      data-alert={alert}
      data-bottom={isBottom}
      onClick={onClick}
      onMouseDown={stop}
      onMouseUp={stop}
      style={
        {
          ...style,
          "--modal-offset": offset,
          "--modal-scale": scale,
        } as React.CSSProperties
      }
    >
      {children}
    </Dialog>,
    document.body
  );
}
